import React, {
  ChangeEvent,
  useState,
  MouseEvent,
  useRef,
  useEffect,
} from 'react'

import { useFilter } from '../../contexts/filter/filter'
import ActionTypes from '../../contexts/filter/actions'

import Icon from '../icon'
import SpecialtiesPopup from './popups/specialties-popup'
import AvailabilityPopup from './popups/availability-popup'
import StatesPopup from './popups/states-popup'
import CitiesPopup from './popups/cities-popup'
import Selections from './selections'

import * as S from './styles'

import IProps from './props'
import ICity from '../../interfaces/city'

import {
  daysTranslation,
  periodsTranslation,
  cbFilter,
  IPopupsVisibility,
  ISearch,
} from './utils'

const SearchDoctors = ({
  onSearch,
  specialtyList,
  stateList,
  loadCities,
}: IProps) => {
  const inputSpecialtyRef = useRef<HTMLInputElement>(null)
  const inputStateRef = useRef<HTMLInputElement>(null)
  const inputCityRef = useRef<HTMLInputElement>(null)
  const inputAvailabilityRef = useRef<HTMLInputElement>(null)

  const [popupsVisibility, setPopupsVisibility] = useState<IPopupsVisibility>({
    specialties: false,
    availabilities: false,
    states: false,
    cities: false,
  })

  const [search, setSearch] = useState<ISearch>({
    specialty: '',
    state: '',
    city: '',
    availability: '',
  })

  const [cities, setCities] = useState<ICity[]>([])

  const { filter: state, filterDispatch: dispatch } = useFilter()

  useEffect(() => {
    const fetchCities = async () => {
      const loadedCitiesPromises = state.locations.map(async (location) => {
        const stateId = stateList.find((s) => s.name === location.state)?.id
        if (stateId) {
          const cityData = await loadCities(stateId)
          return cityData.map((city: any) => ({
            state: location.state,
            ...city,
          }))
        }
        return []
      })
      const loadedCities = await Promise.all(loadedCitiesPromises)
      setCities(loadedCities.flat())
    }

    fetchCities()
  }, [state.locations, loadCities, stateList])

  const handleSearchChange = (event: ChangeEvent<HTMLInputElement>) => {
    setSearch({
      ...search,
      [event.target.name]: event.target.value,
    })
  }

  const togglePopupVisibility = (popupName: keyof IPopupsVisibility) => {
    setPopupsVisibility({
      ...popupsVisibility,
      [popupName]: !popupsVisibility[popupName],
    })
  }

  const handleDelete = (payload: any, action: ActionTypes) => {
    dispatch({ type: action, payload })
  }

  const handleToggleVisibility = (
    evt: MouseEvent<HTMLElement>,
    popupName: keyof IPopupsVisibility,
    ref: React.RefObject<HTMLInputElement>,
  ) => {
    const isInsidePopup = evt.target instanceof HTMLElement
      && evt.target.closest(`.${popupName}-popup`)

    if (!isInsidePopup) {
      togglePopupVisibility(popupName)
    }

    ref.current?.focus()
  }

  const handleApply = (type: ActionTypes, payload: any) => {
    dispatch({ type, payload })
  }

  const handleApplyCities = (_cities: ICity[]) => {
    const allCurrentCities = state.locations.flatMap((location) => location.cities.map((city) => ({
      state: location.state,
      name: city,
    })))

    const citiesToDelete = allCurrentCities.filter(
      (currentCity) => !_cities.some(
        (city) => city.state === currentCity.state && city.name === currentCity.name,
      ),
    )

    _cities.forEach((city) => {
      dispatch({
        type: ActionTypes.ADD_CITY,
        payload: {
          state: city.state,
          city: city.name,
        },
      })
    })

    citiesToDelete.forEach((city) => {
      dispatch({
        type: ActionTypes.DELETE_CITY,
        payload: city.name,
      })
    })
  }

  const handleSearch = () => {
    onSearch(state)
  }

  const handleClearFilters = () => {
    dispatch({ type: ActionTypes.RESET_FILTERS })
    setPopupsVisibility({
      specialties: false,
      availabilities: false,
      states: false,
      cities: false,
    })
  }

  const filteredSpecialtyList = cbFilter(specialtyList, search.specialty)

  const filteredStateList = cbFilter(stateList, search.state)

  const filteredCities = cbFilter(cities, search.city)

  return (
    <S.Container className="search-doctors-container">
      <S.Title>Buscar Médicos</S.Title>
      <S.InputsContainer>
        <S.Wrapper>
          <S.InputContainer
            onClick={(evt) => handleToggleVisibility(evt, 'specialties', inputSpecialtyRef)}
            className="specialty-input-container"
            focused={popupsVisibility.specialties}
          >
            <S.Label>Especialidade</S.Label>
            <input
              type="text"
              value={search.specialty}
              onChange={handleSearchChange}
              placeholder={
                popupsVisibility.specialties
                  ? 'Buscar...'
                  : 'Escolha a(s) especialidade(s)'
              }
              name="specialty"
              className="specialty-input"
              ref={inputSpecialtyRef}
            />
            <S.IconContainer>
              <Icon name="arrow-down" />
            </S.IconContainer>
            {popupsVisibility.specialties && (
              <SpecialtiesPopup
                onApply={(specialties) => handleApply(ActionTypes.ADD_SPECIALTIES, specialties)}
                onClose={() => togglePopupVisibility('specialties')}
                selectedSpecialties={state.specialties}
                specialtyList={filteredSpecialtyList}
              />
            )}
          </S.InputContainer>
          <Selections
            selections={state.specialties}
            onDelete={(specialty) => handleDelete(specialty, ActionTypes.DELETE_SPECIALTY)}
          />
        </S.Wrapper>

        <S.Wrapper>
          <S.InputContainer
            onClick={(evt) => handleToggleVisibility(evt, 'states', inputStateRef)}
            className="state-input-container"
            focused={popupsVisibility.states}
          >
            <S.Label>Estado</S.Label>
            <input
              type="text"
              value={search.state}
              onChange={handleSearchChange}
              placeholder={
                popupsVisibility.states ? 'Buscar...' : 'Escolha o(s) estado(s)'
              }
              name="state"
              className="state-input"
              ref={inputStateRef}
            />
            <S.IconContainer>
              <Icon name="arrow-down" />
            </S.IconContainer>
            {popupsVisibility.states && (
              <StatesPopup
                onApply={(states) => handleApply(ActionTypes.ADD_STATE, states)}
                onClose={() => togglePopupVisibility('states')}
                selectedLocations={state.locations}
                stateList={filteredStateList}
              />
            )}
          </S.InputContainer>
          <S.Label>Você pode buscar apenas por estado(s).</S.Label>
          <Selections
            selections={state.locations.map((location) => location.state)}
            onDelete={(_state) => handleDelete(_state, ActionTypes.DELETE_STATE)}
          />
        </S.Wrapper>

        <S.Wrapper>
          <S.InputContainer
            onClick={(evt) => handleToggleVisibility(evt, 'cities', inputCityRef)}
            className="city-input-container"
            focused={popupsVisibility.cities}
          >
            <S.Label>Cidade</S.Label>
            <input
              type="text"
              value={search.city}
              onChange={handleSearchChange}
              placeholder={
                popupsVisibility.cities ? 'Buscar...' : 'Escolha a(s) cidade(s)'
              }
              name="city"
              className="city-input"
              ref={inputCityRef}
            />
            <S.IconContainer>
              <Icon name="arrow-down" />
            </S.IconContainer>
            {popupsVisibility.cities && (
              <CitiesPopup
                onApply={handleApplyCities}
                selectedLocations={state.locations}
                onClose={() => togglePopupVisibility('cities')}
                cityList={filteredCities}
              />
            )}
          </S.InputContainer>
          <Selections
            selections={state.locations
              .map((location) => location.cities)
              .flat()}
            onDelete={(city) => handleDelete(city, ActionTypes.DELETE_CITY)}
          />
        </S.Wrapper>

        <S.Wrapper>
          <S.InputContainer
            onClick={(evt) => handleToggleVisibility(evt, 'availabilities', inputAvailabilityRef)}
            className="availability-input-container"
            focused={popupsVisibility.availabilities}
          >
            <S.Label>Disponibilidade de dia e horário</S.Label>
            <input
              type="text"
              value={search.availability}
              onChange={handleSearchChange}
              placeholder="Escolha a(s) disponibilidade(s)"
              disabled={popupsVisibility.availabilities}
              name="availability"
              className="availability-input"
              ref={inputAvailabilityRef}
            />
            <S.IconContainer>
              <Icon name="arrow-down" />
            </S.IconContainer>
            {popupsVisibility.availabilities && (
              <AvailabilityPopup
                onApply={(availability) => handleApply(ActionTypes.ADD_AVAILABILITY, availability)}
                onClose={() => togglePopupVisibility('availabilities')}
                selectedAvailability={state.availability}
              />
            )}
          </S.InputContainer>
          {Object.keys(state.availability).length > 0 && (
            <div>
              <strong>Disponibilidades Selecionadas:</strong>
              <ul>
                {Object.keys(state.availability).map((day) => {
                  const translatedDay = daysTranslation[day.toLowerCase()]
                  const hasSelected = state.availability[day].selected
                  const selectedPeriods = Object.keys(state.availability[day])
                    .filter(
                      (period) => period !== 'selected' && state.availability[day][period],
                    )
                    .map((period) => periodsTranslation[period])

                  if (hasSelected || selectedPeriods.length > 0) {
                    return (
                      <li style={{ marginLeft: '20px' }}>
                        {translatedDay}
                        :
                        {' '}
                        {selectedPeriods.map(
                          (period, periodIndex, periodsArray) => (
                            <span>
                              {period}
                              {periodIndex < periodsArray.length - 1
                                ? ', '
                                : ''}
                            </span>
                          ),
                        )}
                      </li>
                    )
                  }

                  return null
                })}
              </ul>
            </div>
          )}
        </S.Wrapper>
      </S.InputsContainer>

      <S.DivButtons>
        <S.SearchButton onClick={handleSearch}>Pesquisar</S.SearchButton>
        <S.ClearButton onClick={handleClearFilters}>
          Limpar filtros
        </S.ClearButton>
      </S.DivButtons>
    </S.Container>
  )
}

export default SearchDoctors
